import type { IButtonStyles, IStyle, ITextFieldStyles } from '@fluentui/react';
import { FontSizes, IH2OTheme, Sizes } from '@h2oai/ui-kit';

export interface IShareModalClassNames {
  mainDescription: string;
  sectionHeader: string;
  sectionDescription: string;
  sectionCard: string;
  warningBanner: string;
  warningText: string;
  textField: string;
  textFieldMonospace: string;
  expirationBanner: string;
  expirationText: string;
  expirationLabel: string;
  workspaceLinkIcon: string;
  warningIcon: string;
  s3ReferenceIcon: string;
  temporaryLinkIcon: string;
  expirationIcon: string;
  grantButton: string;
}

export interface IShareModal {
  mainDescription?: IStyle;
  sectionHeader?: IStyle;
  sectionDescription?: IStyle;
  sectionCard?: IStyle;
  warningBanner?: IStyle;
  warningText?: IStyle;
  textField?: ITextFieldStyles;
  textFieldMonospace?: ITextFieldStyles;
  expirationBanner?: IStyle;
  expirationText?: IStyle;
  expirationLabel?: IStyle;
  workspaceLinkIcon?: IStyle;
  warningIcon?: IStyle;
  s3ReferenceIcon?: IStyle;
  temporaryLinkIcon?: IStyle;
  expirationIcon?: IStyle;
  grantButton?: IButtonStyles;
}

export const shareModalStyle = (theme: IH2OTheme): Partial<IShareModal> => ({
  mainDescription: {
    fontSize: FontSizes.medium,
    color: theme.palette?.gray600,
  },
  sectionHeader: {
    fontSize: FontSizes.large,
    fontWeight: 600,
    color: theme.palette?.gray900,
  },
  sectionDescription: { color: theme.palette?.gray600 },
  sectionCard: {
    padding: Sizes.buttonPaddingLeftRightSmall,
    backgroundColor: theme.palette?.gray200,
    border: `1px solid ${theme.palette?.gray300}`,
    borderRadius: Sizes.borderRadius,
  },
  warningBanner: {
    padding: `${Sizes.buttonPaddingLeftRightSmall}px ${Sizes.buttonPaddingLeftRightSmall}px`,
    backgroundColor: theme.palette?.yellow100,
    border: `1px solid ${theme.palette?.yellow300}`,
    borderRadius: Sizes.borderRadius,
  },
  warningText: {
    color: theme.palette?.yellow700,
    fontSize: FontSizes.small,
    fontWeight: 500,
  },
  textField: {
    root: { flexGrow: 1 },
    field: {
      backgroundColor: theme.palette?.white,
      border: `1px solid ${theme.palette?.gray300}`,
      borderRadius: Sizes.borderRadius,
      padding: Sizes.buttonPaddingLeftRightSmall,
    },
  } as ITextFieldStyles,
  textFieldMonospace: {
    root: { flexGrow: 1 },
    field: {
      backgroundColor: theme.palette?.white,
      border: `1px solid ${theme.palette?.gray300}`,
      borderRadius: Sizes.borderRadius,
      padding: Sizes.buttonPaddingLeftRightSmall,
      fontFamily: 'monospace',
      fontSize: FontSizes.xsmall,
    },
  } as ITextFieldStyles,
  expirationBanner: {
    padding: `${Sizes.buttonPaddingLeftRightSmall}px ${Sizes.buttonPaddingLeftRightSmall}px`,
    backgroundColor: theme.palette?.blue100,
    border: `1px solid ${theme.palette?.blue300}`,
    borderRadius: Sizes.borderRadius,
  },
  expirationText: {
    color: theme.palette?.blue700,
    fontWeight: 500,
  },
  expirationLabel: {
    fontSize: FontSizes.xsmall,
    fontWeight: 600,
    color: theme.palette?.gray900,
    textTransform: 'uppercase',
  },
  workspaceLinkIcon: {
    color: theme.palette?.blue500,
    fontSize: '18px',
  },
  warningIcon: {
    color: theme.palette?.yellow700,
    fontSize: '16px',
  },
  s3ReferenceIcon: {
    color: theme.palette?.green600,
    fontSize: '18px',
  },
  temporaryLinkIcon: {
    color: theme.palette?.red500,
    fontSize: '18px',
  },
  expirationIcon: {
    color: theme.palette?.blue700,
    fontSize: '16px',
  },
  grantButton: {
    root: {
      minWidth: 120,
    },
  } as IButtonStyles,
});
